import {
  CACHE_LAYOUT,
  CACHE_LAYOUT_FIELD,
  CACHE_REGION,
  CACHE_ELEMENT,
  REMOVE_CACHED_ELEMENT,
  SET_ENTITIES,
  MERGE_ENTITIES,
  UPDATE_ENTITY,
  REMOVE_ALL_ENTITY_MODS,
} from '@constants/mutations'
import { SITE_SLUG } from '@constants/siteDetails'
import Store from '@store/store'

import api from '@services/api'
import Vue from 'vue'
import _get from 'lodash/get'

export const state = {
  cachedLayouts: {},
  cachedRegions: {},
  cachedElements: {},
  cache: {
    usage: {},
    queries: {},
  },
  entities: {},
  byId: {},
}

export const getters = {
  getLayoutBySlug: (state) => (layoutSlug) => {
    return (
      Object.values(state.cachedLayouts).find((p) => p.slug === layoutSlug) ||
      false
    )
  },
  getLayout: (state) => (layoutId) => state.cachedLayouts[layoutId] || false,
  getLayouts: (state) => (ids) =>
    !ids ? [] : ids.map((id) => state.cachedLayouts[id]),
  getRegion: (state) => (regionId) => state.cachedRegions[regionId],
  getElement: (state) => (elementId) => state.cachedElements[elementId],
  dirtyLayoutElementCount: (state, getters, rootState) => {
    const mods = _get(rootState, 'entities.mods.layoutElements', {})
    return Object.keys(mods).length
  },
}

export const mutations = {
  [SET_ENTITIES](state, entities) {
    state.entities = entities
  },
  [MERGE_ENTITIES](state, updatedEntities) {
    state.entities = { ...state.entities, ...updatedEntities }
  },
  [CACHE_LAYOUT](state, newLayout) {
    Vue.set(state.cachedLayouts, newLayout.id, newLayout)
    Vue.set(state.entities, newLayout.id, newLayout)
  },
  [CACHE_LAYOUT_FIELD](state, { id, field, value }) {
    Vue.set(state.cachedLayouts[id], field, value)
  },
  [UPDATE_ENTITY](state, { id, key, val }) {
    Vue.set(state.entities[id], key, val)
  },
  [CACHE_REGION](state, newRegion) {
    newRegion.elements.forEach((element) => {
      Vue.set(state.cachedElements, element.id, element)
      if (
        element.type === 'related' &&
        element.related_model_type === 'MenuSection'
      ) {
        Store.dispatch(
          'sections/processSectionResponse',
          { data: element.related },
          { root: true }
        )
      }
    })
    Vue.set(state.cachedRegions, newRegion.id, newRegion)
  },
  [CACHE_ELEMENT](state, newElement) {
    Vue.set(state.cachedElements, newElement.id, newElement)
  },
  [REMOVE_CACHED_ELEMENT](state, elementId) {
    Vue.delete(state.cachedElements, elementId)
    Object.values(state.cachedRegions).forEach((region) => {
      if (!region.elements) return
      const idx = region.elements.findIndex((el) => el.id === elementId || el === elementId)
      if (idx !== -1) {
        region.elements.splice(idx, 1)
      }
    })
  },
}

export const actions = {
  async fetchLayoutBySlug({ commit, getters, rootState }, { layoutSlug }) {
    // 1. Check if we've already fetched and cached the layout.
    const matchedLayout = getters.getLayoutBySlug(layoutSlug)
    if (matchedLayout) {
      return matchedLayout
    }
    // 2. Fetch the layout from the API and cache it in case
    //    we need it again in the future.
    try {
      const response = await api.get(
        `/path-to-model/${SITE_SLUG}/layouts/${layoutSlug}`
      )

      const layout = response.data

      commit(CACHE_LAYOUT, layout)
      layout.regions.forEach((region) => {
        commit(CACHE_REGION, region)
      })
      return layout
    } catch (err) {
      return err
    }
  }, // fetchLayoutBySlug

  async fetchLayout({ commit, getters, rootState }, { layoutId }) {
    // 1. Check if we've already fetched and cached the layout.
    const matchedLayout = getters.getLayout(layoutId)
    if (matchedLayout) {
      return matchedLayout
    }
    // 2. Fetch the layout from the API and cache it in case
    //    we need it again in the future.
    try {
      const response = await api.get(`layouts/${layoutId}`)

      const layout = response.data

      commit(CACHE_LAYOUT, layout)
      layout.regions.forEach((region) => {
        commit(CACHE_REGION, region)
      })
      return layout
    } catch (err) {
      return err
    }
  }, // fetchLayout

  async fetchRegion({ commit, getters, rootState }, { regionId }) {
    // 1. Check if we've already fetched and cached the layout.
    const matchedRegion = getters.getRegion(regionId)
    if (matchedRegion) {
      return matchedRegion
    }
    // 2. Fetch the layout from the API and cache it in case
    //    we need it again in the future.
    try {
      const response = await api.get(`/layout-regions/${regionId}`)

      const region = response.data

      commit(CACHE_REGION, region)
      return region
    } catch (err) {
      return err
    }
  },

  async fetchElement({ commit, getters, rootState }, { elementId }) {
    // 1. Check if we've already fetched and cached the layout.
    const matchedElement = getters.getElement(elementId)
    if (matchedElement) {
      return matchedElement
    }
    // 2. Fetch the layout from the API and cache it in case
    //    we need it again in the future.
    try {
      const response = await api.get(`/layout-elements/${elementId}`)

      const element = response.data

      commit(CACHE_ELEMENT, element)
      return element
    } catch (err) {
      return err
    }
  },

  async updateLayout({ commit }, data) {
    try {
      const response = await api.patch(`/layouts/${data.id}`, data)

      const updatedLayout = response.data

      commit(CACHE_LAYOUT, updatedLayout)

      return updatedLayout
    } catch (err) {
      return err
    }
  },

  async updateRegion({ commit, getters, rootState }, { regionId, data }) {
    try {
      const response = await api.patch(`/layout-regions/${regionId}`, data)

      const updatedRegion = response.data

      commit(CACHE_REGION, updatedRegion)
      return updatedRegion
    } catch (err) {
      return err
    }
  },

  async updateElement({ commit, rootState }, elementData) {
    try {
      const response = !elementData.id
        ? await api.post(
            `sites/${rootState.site.id}/layout-elements`,
            elementData
          )
        : await api.patch(`/layout-elements/${elementData.id}`, elementData)

      const updatedElement = response.data

      commit(CACHE_ELEMENT, updatedElement)

      return updatedElement
    } catch (err) {
      return err
    }
  },

  async deleteElement({ commit, state }, element) {
    // Capture region positions before optimistic removal so we can restore on failure
    const regionSnapshots = []
    Object.values(state.cachedRegions).forEach((region) => {
      if (!region.elements) return
      const idx = region.elements.findIndex((el) => el.id === element.id || el === element.id)
      if (idx !== -1) {
        regionSnapshots.push({ regionId: region.id, index: idx })
      }
    })

    commit(REMOVE_CACHED_ELEMENT, element.id)
    try {
      await api.delete(`/layout-elements/${element.id}`)
      // Unpublished edits to a deleted element can't be published
      commit(`entities/${REMOVE_ALL_ENTITY_MODS}`, {
        entityName: 'layoutElements',
        entityId: element.id,
      }, { root: true })
    } catch (err) {
      commit(CACHE_ELEMENT, element)
      // Restore the element into region arrays at its original position
      regionSnapshots.forEach(({ regionId, index }) => {
        const region = state.cachedRegions[regionId]
        if (region && region.elements) {
          region.elements.splice(index, 0, element)
        }
      })
      return err
    }
  },

  discardElementChanges({ commit, rootState }) {
    const layoutElementMods = _get(rootState, 'entities.mods.layoutElements', {})
    Object.keys(layoutElementMods).forEach((elementId) => {
      commit(`entities/${REMOVE_ALL_ENTITY_MODS}`, {
        entityName: 'layoutElements',
        entityId: elementId,
      }, { root: true })
    })
  },

  // Resolves to the number of elements that failed to publish
  async publishLayoutElements({ commit, state, rootState }) {
    const layoutElementMods = _get(rootState, 'entities.mods.layoutElements')
    if (!layoutElementMods) return 0

    let failedCount = 0
    const elementIds = Object.keys(layoutElementMods)
    for (const elementId of elementIds) {
      const cached = state.cachedElements[elementId]
      if (!cached) continue

      const elementMods = layoutElementMods[elementId]
      const merged = {
        ...cached,
        title: elementMods.title !== undefined ? elementMods.title : cached.title,
        content: {
          ...cached.content,
          text: elementMods.text !== undefined ? elementMods.text : cached.content.text,
        },
      }

      try {
        const response = await api.patch(`/layout-elements/${elementId}`, merged)
        commit(CACHE_ELEMENT, response.data)
        commit(`entities/${REMOVE_ALL_ENTITY_MODS}`, {
          entityName: 'layoutElements',
          entityId: elementId,
        }, { root: true })
      } catch (err) {
        // leave mods in place on failure so user can retry
        failedCount++
      }
    }
    return failedCount
  },

  // shouldApply is asked once the response is back, because menu edit can open
  // while the request is out, and this response would replace those sections
  async checkForUpdates({ commit }, { layout, shouldApply }) {
    try {
      const { data: updatedLayout } = await api.get(`/layouts/${layout.id}`)
      if (updatedLayout && shouldApply()) {
        commit(CACHE_LAYOUT, updatedLayout)
        updatedLayout.regions.forEach((region) => {
          commit(CACHE_REGION, region)
        })
      }
    } catch (error) {
      // Nothing changes, and the next poll tries again
    }
  }, // checkForUpdates
} // actions
